"use client";

import React, { useState, useEffect } from "react";
import Image from "next/image";
import { m as motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import { useTheme } from "next-themes";
import { type BlogPost } from "@/lib/data/blogs";
import { Badge } from "@/components/ui/badge";
import { ThemeSwitcher } from "@/components/ThemeSwitcher";
import dynamic from "next/dynamic";

const MarkdownRenderer = dynamic(
  () => import("@/components/blog/MarkdownRenderer"),
  {
    ssr: false,
    loading: () => (
      <div className="animate-pulse bg-muted/20 h-96 w-full rounded-2xl border border-border" />
    ),
  }
);
import BlogFooter from "@/components/blog/BlogFooter";
import ReadingProgress from "@/components/blog/ReadingProgress";
import TableOfContents from "@/components/blog/TableOfContents";
import {
  Calendar,
  ArrowLeft,
  Share2,
  Twitter,
  Linkedin,
  Facebook,
  Link as LinkIcon,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  ExternalLink,
  Clock,
  User,
  Hash,
  MessageCircle,
  ArrowRight,
  Bookmark,
  Heart,
} from "lucide-react";
import Link from "next/link";
import { getCTAByKey } from "@/lib/actions/cta";
import { subscribeEmail } from "@/lib/actions/subscribe";
import type { CTASettings } from "@/lib/actions/cta";
import BlogHeader from "@/components/blog/BlogHeader";
import { CodeBackground } from "@/components/ui/code-background";
import { cn } from "@/lib/utils";
import { getInternalBlogLink, getBlogTagUrl } from "@/lib/utils/blog-url";
import { SEO_CONFIG } from "@/lib/seo-config";

interface BlogPostClientProps {
  post: BlogPost;
  relatedPosts?: BlogPost[];
  navigation?: { prev: any; next: any };
}

export default function BlogPostClient({
  post,
  relatedPosts = [],
  navigation = { prev: null, next: null },
}: BlogPostClientProps) {
  const { theme, resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [copied, setCopied] = useState(false);
  const [liked, setLiked] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [navImageError, setNavImageError] = useState({ prev: false, next: false });
  const [relatedImageError, setRelatedImageError] = useState<Record<string, boolean>>({});
  const [authorAvatarError, setAuthorAvatarError] = useState(false);
  const [footerAuthorAvatarError, setFooterAuthorAvatarError] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Fix: Reset scroll to top when navigating between blogs
  useEffect(() => {
    if (typeof window !== "undefined") {
      // Small timeout to ensure content has rendered
      const timer = setTimeout(() => {
        if ((window as any).lenis) {
          (window as any).lenis.scrollTo(0, { immediate: true });
        } else {
          window.scrollTo(0, 0);
        }
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [post.id, post.slug]);

  const wordCount = post.content.split(/\s+/).length;
  const calculatedReadingTime = Math.max(1, Math.ceil(wordCount / 200));

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  const handleShare = async (platform: string) => {
    const text = `Check out this article: ${post.title}`;
    const url = shareUrl;
    switch (platform) {
      case "twitter":
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(
            text
          )}&url=${encodeURIComponent(url)}`,
          "_blank"
        );
        break;
      case "linkedin":
        window.open(
          `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
            url
          )}`,
          "_blank"
        );
        break;
      case "copy":
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        break;
    }
  };

  return (
    <div className="min-h-screen bg-background selection:bg-primary/20 relative">
      <CodeBackground />
      <ReadingProgress />
      <BlogHeader />

      {/* Hero Section - Editorial Style with Background */}
      <section className="relative pt-20 pb-20 md:pt-28 md:pb-32 overflow-hidden border-b border-white/5">
        {/* Animated Background Elements - Subtler */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[80%] bg-[radial-gradient(circle_at_center,rgba(var(--primary-rgb),0.05)_0%,transparent_70%)]" />
          <div className="absolute -top-[5%] -left-[5%] w-[30%] h-[30%] bg-primary/3 rounded-full blur-[100px]" />
          <div className="absolute -bottom-[5%] -right-[5%] w-[30%] h-[30%] bg-secondary/3 rounded-full blur-[100px]" />
        </div>

        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <div className="flex flex-col items-center text-center">
            {/* Back Button - Sleek Glass */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="mb-12 self-start"
            >
              <Link
                href="/blogs"
                className="group flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-xs font-bold text-muted-foreground hover:text-primary hover:border-primary/30 hover:bg-white/10 transition-all backdrop-blur-md"
              >
                <ChevronLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
                Back to {SEO_CONFIG.blogName}
              </Link>
            </motion.div>

            {/* Category Badge - High End */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary font-bold text-[10px] uppercase tracking-[0.2em] mb-8 shadow-[0_0_20px_-5px_rgba(var(--primary-rgb),0.3)]"
            >
              <Sparkles className="w-3.5 h-3.5" />
              {post.category}
            </motion.div>

            {/* Title - Striking yet Balanced */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
              className="text-3xl md:text-5xl lg:text-6xl font-black tracking-tight text-foreground leading-[1.1] mb-8 max-w-5xl"
            >
              {post.title}
            </motion.h1>

            {/* Excerpt - Refined Spacing */}
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed mb-12 font-medium"
            >
              {post.metaDescription || post.excerpt}
            </motion.p>

            {/* Author Badge - Minimalist Glass */}
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
              className="flex flex-wrap items-center justify-center gap-4 py-2 px-6 rounded-full bg-white/[0.03] border border-white/5 backdrop-blur-md"
            >
              <div className="relative w-10 h-10 rounded-full overflow-hidden border-2 border-primary/20 shadow-inner bg-muted">
                {post.author.avatar && !authorAvatarError ? (
                  <Image
                    src={post.author.avatar}
                    alt={post.author.name}
                    fill
                    className="object-cover"
                    onError={() => setAuthorAvatarError(true)}
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-primary/10">
                    <User className="w-5 h-5 text-primary/40" />
                  </div>
                )}
              </div>
              
              <div className="flex flex-col items-start gap-0.5">
                <span className="text-sm font-black text-foreground">{post.author.name}</span>
                <span className="text-[10px] uppercase tracking-widest text-primary font-bold">Creator</span>
              </div>

              <div className="w-[1px] h-6 bg-white/10 mx-2" />

              <div className="flex items-center gap-4 text-xs font-bold text-muted-foreground/80">
                <div className="flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5 text-primary/60" />
                  {new Date(post.date ?? 0).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </div>
                <div className="flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5 text-primary/60" />
                  {calculatedReadingTime} min read
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Main Layout Container */}
      <main className="w-full pb-20 pt-16">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 relative">
            {/* Left Sidebar - Floating Actions (Hidden on Mobile) */}
            <aside className="hidden lg:block lg:col-span-1 relative">
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="sticky top-32 space-y-6 flex flex-col items-center"
              >
                <div className="flex flex-col items-center gap-4 p-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-md shadow-xl">
                  <button
                    onClick={() => setLiked(!liked)}
                    className={cn(
                      "p-3 rounded-full transition-all duration-300 group",
                      liked
                        ? "bg-red-500 text-white shadow-[0_0_20px_rgba(239,68,68,0.4)]"
                        : "hover:bg-primary/10 text-muted-foreground hover:text-primary"
                    )}
                  >
                    <Heart
                      className={cn(
                        "w-5 h-5 transition-transform",
                        liked && "fill-current scale-110"
                      )}
                    />
                  </button>

                  <div className="w-8 h-[1px] bg-white/10" />

                  <button
                    onClick={() => handleShare("twitter")}
                    className="p-3 rounded-full text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all"
                  >
                    <Twitter className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleShare("linkedin")}
                    className="p-3 rounded-full text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all"
                  >
                    <Linkedin className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleShare("copy")}
                    className="p-3 rounded-full text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all relative"
                  >
                    <LinkIcon className="w-5 h-5" />
                    {copied && (
                      <span className="absolute left-full ml-4 px-2 py-1 bg-primary text-primary-foreground text-[10px] font-bold rounded-md animate-in fade-in slide-in-from-left-2 backdrop-blur-md whitespace-nowrap">
                        Link Copied
                      </span>
                    )}
                  </button>
                </div>

                <div className="flex flex-col items-center gap-1">
                  <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/40 vertical-text">Share</span>
                  <div className="w-[1px] h-12 bg-gradient-to-b from-white/10 to-transparent" />
                </div>
              </motion.div>
            </aside>

            {/* Center Content Area */}
            <div className="lg:col-span-8 space-y-8 px-2 md:px-6">
              {/* Cover Image */}
              {post.coverImage && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5, duration: 1 }}
                  className="relative aspect-[21/10] rounded-3xl overflow-hidden border border-border/40 group bg-muted"
                >
                  {post.coverImage && !imageError ? (
                    <>
                      <Image
                        src={post.coverImage}
                        alt={post.title}
                        fill
                        className="object-cover transition-transform duration-1000 group-hover:scale-[1.03]"
                        priority
                        onError={() => setImageError(true)}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-60" />
                    </>
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-muted/50">
                       <Sparkles className="w-12 h-12 text-primary/10" />
                    </div>
                  )}
                  
                  {/* Glass Label */}
                  <div className="absolute bottom-6 left-6 px-4 py-2 rounded-xl bg-black/30 backdrop-blur-md border border-white/10 flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                    <span className="text-[10px] font-bold text-white uppercase tracking-[0.2em]">Featured Resource</span>
                  </div>
                </motion.div>
              )}

              {/* TL;DR / Summary Card - Spacious & Clean */}
              {post.metaDescription && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  className="relative p-8 md:p-12 rounded-[2rem] bg-muted/20 border border-border/50 group"
                >
                  <div className="space-y-6 relative z-10">
                    <div className="flex items-center gap-3 text-primary/60 font-black uppercase tracking-[0.4em] text-[10px]">
                      Quick Overview
                    </div>
                    <p className="text-xl md:text-2xl text-foreground font-medium leading-relaxed tracking-tight max-w-3xl">
                      {post.metaDescription}
                    </p>
                  </div>
                </motion.div>
              )}

              {/* Main Content */}
              <div
                className="prose prose-zinc dark:prose-invert max-w-none 
              prose-headings:text-foreground prose-headings:font-bold prose-headings:tracking-normal
              prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-4
              prose-p:text-sm md:prose-p:text-base prose-p:leading-relaxed
              prose-strong:text-foreground prose-strong:font-bold
              prose-a:text-primary prose-a:font-bold prose-a:no-underline hover:prose-a:underline
              prose-code:text-primary prose-code:bg-primary/5 prose-code:px-1.5 prose-code:rounded
              prose-img:rounded-2xl prose-img:shadow-lg"
              >
                <MarkdownRenderer content={post.content} />
              </div>

              {/* Tags and Metadata Footer */}
              <div className="pt-8 border-t border-border flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag) => (
                    <Link
                      key={tag}
                      href={getBlogTagUrl(tag)}
                      className="px-3 py-1.5 rounded-lg bg-muted/50 border border-border text-xs font-bold text-muted-foreground hover:text-primary hover:border-primary/50 transition-all flex items-center gap-2"
                    >
                      <Hash className="w-3 h-3 text-primary" />
                      {tag}
                    </Link>
                  ))}
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleShare("twitter")}
                    className="p-2 text-muted-foreground hover:text-primary transition-colors"
                  >
                    <Twitter className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleShare("linkedin")}
                    className="p-2 text-muted-foreground hover:text-primary transition-colors"
                  >
                    <Linkedin className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleShare("copy")}
                    className="p-2 text-muted-foreground hover:text-primary transition-colors relative"
                  >
                    <LinkIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Author Card */}
              <div className="mt-12 p-8 md:p-10 rounded-2xl bg-gradient-to-br from-muted/50 to-muted/20 border border-border/50 relative overflow-hidden group">
                <div className="flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-8">
                  <div className="relative w-20 h-20 md:w-24 md:h-24 rounded-xl overflow-hidden border-4 border-background shadow-lg transform transition-transform group-hover:scale-105 bg-muted">
                    {post.author.avatar && !footerAuthorAvatarError ? (
                      <Image
                        src={post.author.avatar}
                        alt={post.author.name}
                        fill
                        className="object-cover"
                        onError={() => setFooterAuthorAvatarError(true)}
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center bg-primary/10">
                        <User className="w-10 h-10 text-primary/40" />
                      </div>
                    )}
                  </div>
                  <div className="space-y-3 flex-1">
                    <div className="space-y-1">
                      <h3 className="text-xl md:text-2xl font-black text-foreground">
                        {post.author.name}
                      </h3>
                      <p className="text-primary font-bold tracking-widest text-[10px] uppercase">
                        {post.author.role}
                      </p>
                    </div>
                    <p className="text-muted-foreground text-sm leading-relaxed max-w-xl font-medium">
                      Building digital experiences at the intersection of design
                      and code. Sharing weekly insights on engineering,
                      productivity, and the future of tech.
                    </p>
                    <div className="flex gap-3 pt-1">
                      <Link
                        href="https://twitter.com/sachinsharmadev"
                        target="_blank"
                        className="text-muted-foreground hover:text-primary transition-colors"
                      >
                        <Twitter className="w-4 h-4" />
                      </Link>
                      <Link
                        href="https://linkedin.com/in/sachinsharmadev"
                        target="_blank"
                        className="text-muted-foreground hover:text-primary transition-colors"
                      >
                        <Linkedin className="w-4 h-4" />
                      </Link>
                      <Link
                        href="mailto:contact@sachinsharma.dev"
                        className="text-muted-foreground hover:text-primary transition-colors"
                      >
                        <MessageCircle className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Sidebar - Sticky TOC (Hidden on smaller screens) */}
            <aside className="hidden lg:block lg:col-span-3">
              <div className="sticky top-32 space-y-10">
                <TableOfContents content={post.content} />


                {/* Related Mini-Posts in Sidebar */}
                {relatedPosts.length > 0 && (
                  <div className="space-y-4">
                    <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground border-b border-border pb-3">
                      Related Reading
                    </h3>
                    <div className="space-y-4">
                      {relatedPosts.slice(0, 2).map((p) => (
                        <Link
                          key={p.slug}
                          href={getInternalBlogLink(`/${p.slug}`)}
                          className="group block space-y-2"
                        >
                          <div className="relative aspect-[16/9] rounded-lg overflow-hidden border border-border bg-muted">
                            {p.coverImage && !relatedImageError[p.slug] ? (
                              <Image
                                src={p.coverImage}
                                alt={p.title}
                                fill
                                className="object-cover transition-transform group-hover:scale-105"
                                onError={() => setRelatedImageError(prev => ({ ...prev, [p.slug]: true }))}
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                                <span className="text-[10px] text-muted-foreground uppercase font-black opacity-20">
                                  No image
                                </span>
                              </div>
                            )}
                          </div>
                          <h4 className="text-xs font-bold group-hover:text-primary transition-colors line-clamp-2 leading-snug">
                            {p.title}
                          </h4>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </aside>
          </div>
        </div>
      </main>

      {/* Navigation Footer - Next/Prev Article */}
      <section className="border-t border-border mt-16">
        <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border">
          {navigation.prev ? (
            <Link
              href={getInternalBlogLink(`/${navigation.prev.slug}`)}
              className="group relative flex flex-col justify-center min-h-[220px] p-8 md:p-12 overflow-hidden bg-background hover:bg-muted/5 transition-all duration-500"
            >
              {navigation.prev.cover_image && !navImageError.prev && (
                <div className="absolute inset-0 z-0 opacity-0 group-hover:opacity-10 transition-opacity duration-700">
                  <Image
                    src={navigation.prev.cover_image}
                    alt=""
                    fill
                    className="object-cover grayscale"
                    onError={() => setNavImageError(prev => ({ ...prev, prev: true }))}
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-background to-transparent" />
                </div>
              )}

              <div className="relative z-10 flex flex-col gap-4">
                <div className="flex items-center gap-2 text-[11px] font-bold text-muted-foreground/60 uppercase tracking-[0.2em]">
                  <ArrowLeft className="w-3.5 h-3.5 transition-transform duration-300 group-hover:-translate-x-1.5" />
                  <span>Previous Article</span>
                </div>
                <div className="space-y-3">
                  <h4 className="text-xl md:text-2xl font-black text-foreground group-hover:text-primary transition-colors duration-300 leading-tight max-w-md">
                    {navigation.prev.title}
                  </h4>
                  {navigation.prev.excerpt && (
                    <p className="text-muted-foreground text-sm line-clamp-1 font-medium max-w-sm">
                      {navigation.prev.excerpt}
                    </p>
                  )}
                </div>
              </div>
            </Link>
          ) : (
            <div className="hidden md:block bg-muted/5" />
          )}

          {navigation.next ? (
            <Link
              href={getInternalBlogLink(`/${navigation.next.slug}`)}
              className="group relative flex flex-col justify-center items-end text-right min-h-[220px] p-8 md:p-12 overflow-hidden bg-background hover:bg-muted/5 transition-all duration-500"
            >
              {navigation.next.cover_image && !navImageError.next && (
                <div className="absolute inset-0 z-0 opacity-0 group-hover:opacity-10 transition-opacity duration-700">
                  <Image
                    src={navigation.next.cover_image}
                    alt=""
                    fill
                    className="object-cover grayscale"
                    onError={() => setNavImageError(prev => ({ ...prev, next: true }))}
                  />
                  <div className="absolute inset-0 bg-gradient-to-l from-background to-transparent" />
                </div>
              )}

              <div className="relative z-10 flex flex-col items-end gap-4">
                <div className="flex items-center gap-2 text-[11px] font-bold text-muted-foreground/60 uppercase tracking-[0.2em]">
                  <span>Next Article</span>
                  <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1.5" />
                </div>
                <div className="space-y-3">
                  <h4 className="text-xl md:text-2xl font-black text-foreground group-hover:text-primary transition-colors duration-300 leading-tight max-w-md">
                    {navigation.next.title}
                  </h4>
                  {navigation.next.excerpt && (
                    <p className="text-muted-foreground text-sm line-clamp-1 font-medium max-w-sm">
                      {navigation.next.excerpt}
                    </p>
                  )}
                </div>
              </div>
            </Link>
          ) : (
            <div className="hidden md:block bg-muted/5" />
          )}
        </div>
      </section>


      <BlogFooter />
    </div>
  );
}

